/**
 * The control panel: atlas, projection, colour channel, landmarks, and the interaction mode with
 * its two sub-axes (P9.5, `V15`, `V16`, `V18`).
 *
 * **Interaction mode lives here and above the renderer, never inside it** (§8). `renderer.ts`
 * reports where a click landed; this decides what the app will do about it. A renderer that knew
 * about modes would have to be reimplemented by any replacement.
 *
 * **The colour channels are read off `manifest.columns`, never hardcoded** (`V18`, §5). A future
 * label — a measured MIC, a predicted property — is then a new column in the manifest rather than
 * a new branch in this file. Roles decide the widget: `categorical` and `continuous` are
 * palettes, `boolean` is a landmark overlay, `index` is an address and is never offered.
 */
import type { AtlasListing, AtlasManifest, CuratedSet, Fill, ProjectionSpec, Sampler } from '../api';
import { parseRawProjection, rawProjectionName, validFills } from '../api';
import type { SetView, SetViews } from '../curated';
import { ALL_OFF, CURATED_ORDER, hasLandmarks, isShown } from '../curated';

/**
 * What a click on the map means (`V15`, `V131`).
 *
 * **Exclusive values, not a base mode plus checkboxes.** `decode` invents a latent vector at a
 * coordinate and produces no corpus row, so "decode and also add to the trajectory" has nothing to
 * add — two controls that cannot both be on are one control. `trajectory` is `inspect` *plus* an
 * append, so everything inspect does on a click still happens (P27.2).
 *
 * **`interpolate` is a fourth, and it is a selection gesture rather than an action** (`V143`,
 * P30.8). A click fills one of two endpoint slots; the run itself is a button on the panel, which
 * is what the reader asked for and is also what the shape requires — a walk needs *both* ends
 * before it can be computed, so no single click can be the trigger. Like `trajectory` it is
 * `inspect` plus something: the clicked point is still selected and still pinned, because a reader
 * choosing two endpoints needs to read what they picked.
 */
export type Mode = 'inspect' | 'decode' | 'trajectory' | 'interpolate';

/**
 * The dataset selector (P10.5). **A filter on the `split` column, never a separate atlas**
 * (`plan §4`): the splits are one partition of one corpus, so three atlases would triple the disk
 * and make the three views non-comparable.
 */
export type Dataset = 'full' | 'train' | 'val' | 'test';

export interface ControlState {
  atlasId: string;
  /**
   * A manifest projection's name, **or** a raw-dimension pair as `raw:<i>,<j>` (`V65`, P11.3).
   *
   * One field for both, because a raw view is a projection in every sense the rest of the app
   * cares about: it has a kind, it decides which fills are valid, `/decode` and `/encode` take it
   * by this name, and it is what goes in the URL. A separate `dims` field beside it would be a
   * second source of truth for "what is on screen", and the two would drift the first time one
   * was set without the other.
   */
  projection: string;
  /**
   * A palette column's name, **or `''` for the flat map** (`V154`, P31).
   *
   * `''` was already the initial value and already meant "nothing chosen yet"; what P31 added is
   * that it is now *choosable*, and that `applyChannels` acts on it rather than skipping. The two
   * readings coincide harmlessly — an unconfigured map and a deliberately flat one look the same,
   * and both are honest.
   */
  colour: string;
  landmarks: string | null;
  /**
   * Which curated overlays are drawn, and which of those print their labels (`V150`, P31).
   *
   * **One field rather than six**, so the URL stays short and a set cannot be half-configured into
   * a state where its landmarks are on and the set is off.
   *
   * **This is the specific corner of the app** — three named sets, hardcoded, where every other
   * control here is generated from `manifest.columns` and dispatched on a declared role (`V18`).
   * See `curated.ts`'s header; `PLAN_CURATED_SETS.md §13` names the trigger for generalising it.
   */
  curated: SetViews;
  dataset: Dataset;
  mode: Mode;
  fill: Fill;
  sampler: Sampler;
  samplerParams: Record<string, number>;
}

/** `oop_contracts §3.5`. The knob each sampler takes, and the default the server would use. */
const SAMPLER_PARAM: Record<Sampler, { name: string; label: string; value: number } | null> = {
  greedy: null,
  temperature: { name: 'temperature', label: 'temperature', value: 1.0 },
  top_k: { name: 'k', label: 'k', value: 50 },
  top_p: { name: 'p', label: 'p', value: 0.9 },
};

/**
 * Columns whose *displayed* name differs from the one in the manifest (`V119`, P19.8).
 *
 * **A display map, not a branch.** `V18`'s rule is that the widget is chosen by `role` and never
 * by name, and this does not touch that: every column is still offered, still by role, and the
 * value in the `<option>` is still the manifest's own name — which is what goes to the server and
 * into the URL. Only the text a reader sees is overridden.
 *
 * `split` reads as a verb and as half a dozen other things; `dataset split` is what it is, and it
 * matches the `dataset` selector above it that filters on the same column. The precedent is `V79`
 * — the health panel's displayed label and its internal name are already allowed to differ.
 *
 * A column not listed here shows its own name, so this stays empty for anything a future atlas
 * declares rather than becoming a table that has to be kept in step with one.
 */
const COLUMN_LABEL: Record<string, string> = {
  split: 'dataset split',
};

const labelFor = (name: string): string => COLUMN_LABEL[name] ?? name;

/**
 * The help line for a mode that has one, or `undefined` to fall through to the fill's (`V131`).
 *
 * `inspect` is deliberately absent: it is the application default and prints nothing, which is
 * what it printed before this table existed.
 */
const MODE_HELP: Partial<Record<Mode, string>> = {
  // **The diamonds are named here because nothing else names them** (`V132`, P28). A decoded point
  // joins the walk on a click on the diamond itself; a click merely near one resolves to a corpus
  // point, because `nearestRendered` searches the tiles and a generated point is not in them.
  trajectory:
    'click points in order, start to end — corpus points, or a decoded point’s diamond; the trajectory panel lists them and marks the residues that differ',
  // **The two facts a reader needs before reading the result**, and neither is inferable from the
  // screen: the walk is computed in the model's own space, and what that draws differs by
  // projection kind — on PCA and raw views the steps land on a straight, evenly spaced line by
  // construction (the transform is affine), so the coordinates carry nothing the two endpoints did
  // not; on UMAP the same latents draw a curve 1.45x the chord with step spacing varying by 6.9x.
  interpolate:
    'click two points, then interpolate: the walk is computed in the latent space, never between the coordinates on screen',
};

const FILL_HELP: Record<Fill, string> = {
  hybrid:
    'exact coordinates on the visible axes, remaining dimensions from the nearest dataset point',
  nearest: 'the nearest dataset point’s own stored latent — honest, but it cannot explore',
  inverse: 'the projection’s own inverse; hidden dimensions go to the dataset mean',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child);
  return node;
}

/**
 * Dimension indices, most active first (P11.3).
 *
 * A stable sort on index breaks ties, so an atlas with no `latent_stats.json` — `activity` is
 * `null` — gets plain index order rather than an arbitrary one that changes between reloads.
 */
/**
 * A projection name -> its `kind`, covering raw pairs that have no manifest entry (`V65`).
 *
 * `undefined` for a name that is neither, which is a projection the manifest does not declare —
 * the caller leaves the fill alone and the server refuses it, per §0.5.
 */
function kindOf(manifest: AtlasManifest, name: string): ProjectionSpec['kind'] | undefined {
  if (parseRawProjection(name) !== null) return 'raw';
  return manifest.projections.find((p) => p.name === name)?.kind;
}

/**
 * The fill to fall back to when the selected one is undefined for the projection on screen
 * (`V110`, §3.4).
 *
 * **Ordered by how much of the click each one keeps**, which is the order §3.4's matrix reads:
 * `hybrid` writes both clicked coordinates into `z` and takes the rest from a neighbour;
 * `inverse` maps the click through the projection's own inverse; `nearest` discards the click
 * entirely and returns a stored row. So the fallback under UMAP is **`inverse`** rather than
 * `nearest` — `V47` exists precisely so that UMAP has a usable inverse, and falling back past it
 * to `nearest` hands the decoder tool the one fill that cannot explore.
 *
 * The list is a total order over `Fill`, so this always returns something: `nearest` supports
 * every projection by construction.
 */
const FILL_PREFERENCE: readonly Fill[] = ['hybrid', 'inverse', 'nearest'];

function preferredFill(allowed: Record<Fill, boolean>): Fill {
  return FILL_PREFERENCE.find((name) => allowed[name]) ?? 'nearest';
}

function byActivity(latentDim: number, activity: readonly number[] | null): number[] {
  const order = Array.from({ length: latentDim }, (_, d) => d);
  if (activity === null || activity.length !== latentDim) return order;
  return order.sort((a, b) => (activity[b] ?? 0) - (activity[a] ?? 0));
}

function option(value: string, label: string, selected: boolean, disabled = false): HTMLOptionElement {
  const node = el('option', { value }, label);
  node.selected = selected;
  node.disabled = disabled;
  return node;
}

export interface Controls {
  render(listing: AtlasListing, manifest: AtlasManifest): void;
  state(): ControlState;
  /** Set fields without emitting. For seeding from the URL before the first render. */
  set(patch: Partial<ControlState>): void;
  projectionSpec(manifest: AtlasManifest): ProjectionSpec | undefined;
  /**
   * Per-dimension activity — `Var_x(mu_d)` — used to order the raw-dimension pickers (P11.3).
   *
   * **Ordering by activity rather than by index is the whole point of the widget.** At `D = 32`
   * the informative pairs are wherever the encoder actually put variance, and dimensions 0 and 1
   * have no claim to be those; on this corpus the most active dimension holds 1.03 % of its
   * budget and the least 0.044 %, so an index-ordered list opens on two dimensions that are
   * indistinguishable from noise. `null` when the atlas has no `latent_stats.json`, and the list
   * falls back to index order — stated in the label rather than silently pretending.
   */
  setActivity(perDim: readonly number[] | null): void;
  /**
   * The curated sets `GET /curated` served, or `[]` (`V150`, P31).
   *
   * **Not a manifest read**, unlike every other list this panel renders, and that asymmetry is the
   * point: a curated set belongs to the installation rather than to an atlas, so it does not
   * change when the reader switches atlas and it is not in `render`'s arguments. Set once, after
   * the fetch; `render` picks it up from there.
   */
  setCuratedSets(sets: readonly CuratedSet[]): void;
}

export function createControls(
  root: HTMLElement,
  onChange: (changed: keyof ControlState, state: ControlState) => void,
  /**
   * Appended to every widget id, so a second pane's controls do not collide with the first's
   * (P12.1). Empty for pane A, which is what keeps every existing selector — `#mode-select`,
   * `#projection-select` — meaning the same thing it did before side-by-side existed.
   */
  suffix = '',
): Controls {
  const id = (name: string) => `${name}${suffix}`;
  let activity: readonly number[] | null = null;
  /**
   * The sets `GET /curated` served, or `[]` when this installation has none.
   *
   * **Empty renders no group at all**, rather than three disabled rows. An installation that has
   * not been given the lists is a working installation, and three dead controls would read as a
   * broken one — the same judgement `api.curatedLists` records about a 409 not being an error.
   */
  let curatedSets: readonly CuratedSet[] = [];
  const state: ControlState = {
    atlasId: '',
    projection: '',
    colour: '',
    landmarks: null,
    curated: { ...ALL_OFF },
    dataset: 'full',
    // **Inspect is the application default and the decoder tool is opt-in** (`V14`, §3.0).
    // Inspect is the honest one: everything it shows is a real sequence that was really in the
    // corpus, at a position that is really its own. Generating a new sequence is something the
    // user asks for by picking up a tool, never something a stray click does.
    mode: 'inspect',
    // The **decoder tool's** default, which is allowed to be the adventurous one precisely because
    // by the time it applies the user has already said they want to generate (`V15`).
    fill: 'hybrid',
    // Greedy, because the map must be stable: the same click returning a different sequence on
    // different visits is confusing in a way that outweighs sample diversity (`V16`).
    sampler: 'greedy',
    samplerParams: {},
  };

  const emit = (changed: keyof ControlState) => onChange(changed, { ...state });

  /**
   * The curated overlay group: one row per served set, each a show box and a labels box.
   *
   * **Two checkboxes rather than a three-way select**, because they are two independent questions
   * and one of them only exists while the other is true — which a disabled box says plainly and an
   * option list does not. `SetView` collapses them into one storable value; the widgets are the
   * decomposition of it.
   *
   * Returns `[]` when nothing was served, so the group vanishes rather than showing dead controls.
   */
  function curatedRows(): HTMLElement[] {
    const byId = new Map(curatedSets.map((s) => [s.id, s]));
    const rows = CURATED_ORDER.flatMap((setId) => {
      const set = byId.get(setId);
      if (set === undefined) return [];

      const show = el('input', { type: 'checkbox', id: id(`curated-${setId}`) }) as HTMLInputElement;
      show.checked = isShown(state.curated[setId]);
      const marks = el('input', {
        type: 'checkbox',
        id: id(`curated-${setId}-landmarks`),
      }) as HTMLInputElement;
      marks.checked = hasLandmarks(state.curated[setId]);
      // A set that is not drawn cannot have labels. Disabled rather than hidden, so the control
      // stays in place and the reader can see what turning the set on will offer them.
      marks.disabled = !show.checked;

      const write = (next: SetView): void => {
        state.curated = { ...state.curated, [setId]: next };
        emit('curated');
      };
      show.addEventListener('change', () => {
        // **Turning a set off forgets its label setting**, deliberately: `off` is one state, not
        // two, so a set cannot come back on carrying two hundred labels the reader last saw a
        // session ago and has no reason to expect.
        write(show.checked ? 'on' : 'off');
      });
      marks.addEventListener('change', () => {
        write(marks.checked ? 'on+landmarks' : 'on');
      });

      return [
        el(
          'label',
          { class: `curated-row set-${setId}` },
          show,
          el('span', { class: 'curated-swatch' }),
          el('span', { class: 'curated-caption' }, set.caption),
          el('span', { class: 'curated-count' }, `${set.rows.length}`),
          el('span', { class: 'curated-marks' }, marks, ' labels'),
        ),
      ];
    });
    return rows.length === 0 ? [] : [el('div', { class: 'curated-group' }, ...rows)];
  }

  function render(listing: AtlasListing, manifest: AtlasManifest): void {
    root.replaceChildren();

    const atlases = el('select', { id: id('atlas-select') });
    for (const m of listing.atlases) {
      const resident = listing.resident.includes(m.atlas_id) ? ' ●' : '';
      atlases.append(option(m.atlas_id, `${m.atlas_id}${resident}`, m.atlas_id === state.atlasId));
    }
    atlases.addEventListener('change', () => {
      state.atlasId = atlases.value;
      emit('atlasId');
    });

    // The dimension order the raw pickers use, and the pair that is on screen if one is.
    const order = byActivity(manifest.latent_dim, activity);
    const pair = parseRawProjection(state.projection);
    // **The raw entry's value is the pair, not a sentinel** (`V65`). `state.projection` is then
    // always a name `/decode` accepts and always what belongs in the URL, with no second field
    // to keep in step.
    const shown: [number, number] = pair ?? [order[0] ?? 0, order[1] ?? 1];

    const projections = el('select', { id: id('projection-select') });
    for (const spec of manifest.projections) {
      projections.append(option(spec.name, `${spec.name} (${spec.kind})`, spec.name === state.projection));
    }
    projections.append(
      option(rawProjectionName(shown[0], shown[1]), 'raw dimensions (raw)', pair !== null),
    );

    const chooseProjection = (name: string) => {
      state.projection = name;
      // §3.4 again: switching to UMAP can invalidate the fill that was selected under PCA. The
      // server would refuse it, which is the backstop — the UI's job is to not send it.
      const kind = kindOf(manifest, name);
      if (kind !== undefined && !validFills(kind)[state.fill]) {
        state.fill = preferredFill(validFills(kind));
      }
      emit('projection');
    };
    projections.addEventListener('change', () => chooseProjection(projections.value));

    /**
     * The two dimension pickers (P11.3).
     *
     * Rendered always and hidden unless a raw pair is on screen, so the panel does not reflow
     * when the projection changes. **`i === j` is excluded rather than merely refused**: a raw
     * projection over one dimension twice is not a degraded view, it is a line, and
     * `RawDimsProjection` raises on it — so the widget must not be able to ask for it.
     */
    const dimSelect = (which: 0 | 1) => {
      const node = el('select', { id: id(which === 0 ? 'dim-i-select' : 'dim-j-select') });
      for (const d of order) {
        const label =
          activity === null ? `dim ${d}` : `dim ${d} — var ${(activity[d] ?? 0).toPrecision(3)}`;
        node.append(option(String(d), label, d === shown[which], d === shown[1 - which]));
      }
      node.addEventListener('change', () => {
        const next: [number, number] = [...shown];
        next[which] = Number(node.value);
        chooseProjection(rawProjectionName(next[0], next[1]));
      });
      return node;
    };
    const dimI = dimSelect(0);
    const dimJ = dimSelect(1);
    const dimLabel = el(
      'label',
      { class: 'dims' },
      'dimensions ',
      dimI,
      dimJ,
    );
    dimLabel.hidden = pair === null;

    const palettes = manifest.columns.filter(
      (c) => c.role === 'categorical' || c.role === 'continuous',
    );
    const colours = el('select', { id: id('colour-select') });
    // **`none` first, and it is not merely the absence of a choice** (`V154`). Every other option
    // here is a column, so before P31 the map was always coloured *by something*; this is the
    // reader saying they want the corpus to carry no information right now, because they are
    // reading what is drawn on top of it. `V84` already records what colouring by a one-value
    // column looks like — this is the honest way to ask for the same flat map.
    colours.append(option('', 'none (flat)', state.colour === ''));
    for (const c of palettes) {
      colours.append(option(c.name, `${labelFor(c.name)} (${c.role})`, c.name === state.colour));
    }
    colours.addEventListener('change', () => {
      state.colour = colours.value;
      emit('colour');
    });

    const flags = manifest.columns.filter((c) => c.role === 'boolean');
    const landmarks = el('select', { id: id('landmark-select') });
    landmarks.append(option('', 'none', state.landmarks === null));
    for (const c of flags) {
      landmarks.append(option(c.name, labelFor(c.name), c.name === state.landmarks));
    }
    landmarks.addEventListener('change', () => {
      state.landmarks = landmarks.value === '' ? null : landmarks.value;
      emit('landmarks');
    });

    // `SPLIT_NAMES` order is `§5`'s contract — 0 train, 1 val, 2 test — and is not re-derived here.
    const datasets = el('select', { id: id('dataset-select') });
    const hasSplit = manifest.columns.some((c) => c.name === 'split');
    for (const name of ['full', 'train', 'val', 'test'] as Dataset[]) {
      datasets.append(option(name, name, name === state.dataset));
    }
    datasets.disabled = !hasSplit;
    datasets.addEventListener('change', () => {
      state.dataset = datasets.value as Dataset;
      emit('dataset');
    });

    const kind = kindOf(manifest, state.projection);
    const allowed = validFills(kind ?? 'pca');
    // **Repaired here and not only in `chooseProjection`** (`V110`). That function runs when the
    // reader picks a projection from the dropdown; the projection is also set from the URL, from
    // the manifest's first entry on an atlas change, and by the comparison pane's `set` — none of
    // which pass through it. An atlas whose first projection is UMAP therefore opened with the
    // default `hybrid` selected and disabled, which is a fill the server would refuse and the
    // dropdown could not be talked out of.
    if (!allowed[state.fill]) state.fill = preferredFill(allowed);

    const modes = el('select', { id: id('mode-select') });
    modes.append(option('inspect', 'inspect — show the stored sequence', state.mode === 'inspect'));
    modes.append(option('decode', 'decoder tool — decode the click', state.mode === 'decode'));
    modes.append(
      option(
        'trajectory',
        'trajectory — add the click to the trajectory',
        state.mode === 'trajectory',
      ),
    );
    modes.append(
      option(
        'interpolate',
        'interpolate — pick two points to walk between',
        state.mode === 'interpolate',
      ),
    );
    modes.addEventListener('change', () => {
      state.mode = modes.value as Mode;
      emit('mode');
    });

    const fills = el('select', { id: id('fill-select') });
    for (const name of ['hybrid', 'nearest', 'inverse'] as Fill[]) {
      const ok = allowed[name];
      const label = ok ? name : `${name} — undefined for ${kind ?? '?'}`;
      fills.append(option(name, label, name === state.fill, !ok));
    }
    fills.disabled = state.mode !== 'decode';
    fills.addEventListener('change', () => {
      state.fill = fills.value as Fill;
      emit('fill');
    });

    // **Never disabled, unlike the fill** (`V110`). The fill only means something for a click that
    // invents a latent vector, so it belongs to the decoder tool; the sampler is applied to every
    // `/decode` call there is, **including inspect mode's "show reconstruction"**, which sends
    // `sampler` with the row it reads. Greying it out in inspect mode hid the one knob that
    // changes what that button returns.
    const samplers = el('select', { id: id('sampler-select') });
    for (const name of ['greedy', 'temperature', 'top_k', 'top_p'] as Sampler[]) {
      samplers.append(option(name, name, name === state.sampler));
    }

    const knob = el('input', { type: 'number', step: '0.05', min: '0', id: id('sampler-param') });
    const knobLabel = el('label', { class: 'knob', for: id('sampler-param') });
    const applyKnob = () => {
      const param = SAMPLER_PARAM[state.sampler];
      if (param === null) {
        knobLabel.hidden = true;
        state.samplerParams = {};
        return;
      }
      knobLabel.hidden = false;
      knobLabel.replaceChildren(`${param.label} `, knob);
      // **Seeded from the state, not from the default** (`V110`). `render` builds a fresh `<input>`
      // whose value is `''`, and mode changes now re-render — so reading only the default would
      // silently reset a temperature the reader had typed every time they picked up the tool.
      if (knob.value === '') knob.value = String(state.samplerParams[param.name] ?? param.value);
      state.samplerParams = { [param.name]: Number(knob.value) };
    };
    samplers.addEventListener('change', () => {
      state.sampler = samplers.value as Sampler;
      state.samplerParams = {};
      knob.value = '';
      applyKnob();
      emit('sampler');
    });
    knob.addEventListener('change', () => {
      applyKnob();
      emit('samplerParams');
    });
    applyKnob();

    // The fill's help under the decoder tool, and the trajectory's own line under it (`V131`).
    // Inspect needs none: it is the default and its behaviour is the map's baseline.
    const help = el('p', { class: 'help' }, MODE_HELP[state.mode] ?? FILL_HELP[state.fill]);

    root.append(
      el('label', {}, 'atlas ', atlases),
      el('label', {}, 'projection ', projections),
      dimLabel,
      el('label', {}, 'colour ', colours),
      el('label', {}, 'landmarks ', landmarks),
      el('label', {}, 'dataset ', datasets),
      ...curatedRows(),
      el('hr', {}),
      el('label', {}, 'click means ', modes),
      el('label', {}, 'fill ', fills),
      el('label', {}, 'sampler ', samplers),
      knobLabel,
      help,
    );
  }

  return {
    render,
    // A **copy**, so a caller cannot mutate the panel's state behind its back and leave the
    // widgets disagreeing with it. `set` is the one way in.
    state: () => ({ ...state }),
    set: (patch) => Object.assign(state, patch),
    // `undefined` for a raw pair, which has no manifest entry by construction (`V12`). The caller
    // synthesises one from what `/rawdims` actually returned — the sample size is the server's
    // answer, not something the manifest could have known (P11.2).
    projectionSpec: (manifest) => manifest.projections.find((p) => p.name === state.projection),
    setActivity: (perDim) => {
      activity = perDim;
    },
    setCuratedSets: (sets) => {
      curatedSets = sets;
    },
  };
}
